import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { DatePipe } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { map, startWith, take, takeUntil } from 'rxjs/operators';
import { combineLatest, Observable, of, ReplaySubject } from 'rxjs';
import { DynamicDrawerService } from '../dynamic-drawer.service';
import { PropertyService } from '../services/property.service';
import { ITagsPropsName } from 'app/services/graphqlApi.service';
import { DataService } from '../data/data.service';
import { PropertyType, Property } from './interfaces/interfaces';
import { EEntityTypes, EPropertyTypes, ETagPropertyDataType } from '../enums';
import { ITranslate } from '../constants';

@Component({
  selector: 'app-create-properties',
  templateUrl: './create-properties.component.html',
  styleUrls: ['./create-properties.component.scss'],
  providers: [DatePipe]
})

export class CreatePropertiesComponent implements OnInit, OnDestroy {
  //#region global declarations and assignments

  readonly destroyed = new ReplaySubject(1);
  isOverride: boolean = false;
  dynamicControls = {};
  isOverrideControl = new FormControl(this.isOverride);
  filteredPropertyNames: Observable<ITagsPropsName[]> = of([]);
  propertyNames: ITagsPropsName[] = []; // distinct union of prop names across all entities
  propertyTypes: PropertyType[] = [];
  previousPropertyName: string = '';
  selectedPropertyType: PropertyType = {} as PropertyType;
  selectedProperty: Property = {} as Property;
  trans: ITranslate = { text: '' }; // for translation

  propertiesForm = this.formBuilder.group({
    'name': ['', Validators.required],
    'dataTypeId': ['', [Validators.required]],
  });

  //#endregion

  //#region constructor and ngOnInit

  constructor(
    private formBuilder: FormBuilder,
    private dynamicDrawerService: DynamicDrawerService<Property>,
    readonly dataService: DataService,
    private readonly translate: TranslateService,
    private readonly propertyService: PropertyService,
    private router: Router,
    private datePipe: DatePipe) {
    // get properties names
    this.getPropertiesNames();

    // property details in edit case
    this.dataService.propertyDetails$.pipe(takeUntil(this.destroyed)).subscribe((property: Property) => {
      if (property) {
        this.selectedProperty = { ...property };
        console.log('this.selectedProperty: ', this.selectedProperty);
        this.isOverride = this.selectedProperty?.isOverride ?? false;
        console.log('this.isOverride: ', this.isOverride);
        this.setDynamicControl(property.propertyType);
        this.selectedPropertyType = { id: property.dataTypeId, name: property.propertyType };
        this.setFormValue();

        if (this.isAssetsPage) {
          this.propertiesForm.disable();
          this.isOverrideControl.setValue(this.isOverride);
          this.editOrDisableFields(this.isOverride);
        }

        if (this.isModelPage && property?.disableDeleteAction) {
          this.propertiesForm.disable();
        }

        if (this.isModelPage && this.selectedProperty.propertyType === ETagPropertyDataType.CoordinatesDD) {
          this.getControl('text').disable();
        }

        // in save & add new case, updating the PropertiesNames list
        if (this.previousPropertyName) {
          this.propertyNames.push({ name: this.previousPropertyName });
          this.autoCompletePropName();
        }
      }
    });

    this.isOverrideControl.valueChanges.subscribe(isOverride => {
      this.isOverride = isOverride;
      this.editOrDisableFields(isOverride);
    });
  }

  ngOnInit(): void {
    this.propertyService.getPropertTypes().subscribe((types: PropertyType[]) => {
      if(types.length){
        this.propertyTypes = types;
      }
    });
            // translation
            this.translate.onLangChange.subscribe(langChange => this.getTranslation());
            this.getTranslation();
  }

      /**
   * get translation
   */
      getTranslation(){
        this.translate.get('Component').subscribe(components => this.trans = components );
      }

  //#endregion

  //#region getter functions

  /**
 * is model page
 */
  get isModelPage() {
    return this.router.url.includes(EEntityTypes.MODELS);
  }

  /**
   * is assets page
   */
  get isAssetsPage() {
    return this.router.url.includes(EEntityTypes.ASSETS);
  }

  /**
   * isEdited to detect any change
   */
  get isEdited() {
    let edited = this.selectedProperty.name !== this.getControl('name').value ||
      this.selectedProperty.dataTypeId !== this.getControl('dataTypeId').value;
    if (edited) return edited;

    if (this.selectedProperty.propertyType === 'String' || this.selectedProperty.propertyType === 'Text' || this.selectedProperty.propertyType === 'Coordinates DD') {
      edited = this.selectedProperty.value !== this.getControl('text').value;
    } else if (this.selectedProperty.propertyType === 'Datetime') {
      const propValue = this.selectedProperty.value ? this.datePipe.transform(this.selectedProperty.value, 'yyyy-MM-dd') : '';
      const formValue = this.getControl('dateTime').value ? this.datePipe.transform(this.getControl('dateTime').value, 'yyyy-MM-dd') : '';
      edited = formValue !== propValue;
    }
    return true;
  }

  //#endregion

  //#region editOrDisableFields, setFormValue, onResetProperty, addProperty and propTypeChanged methods

  /**
   * edot or disable fields
   * @param isOverride isOverride
   */
  editOrDisableFields(isOverride: boolean) {
    if (isOverride) {
      if (this.selectedProperty.propertyType === 'String' || this.selectedProperty.propertyType === 'Text' || this.selectedProperty.propertyType === 'Coordinates DD') {
        this.getControl('text').enable();
      } else if (this.selectedProperty.propertyType === 'Datetime') {
        this.getControl('dateTime').enable();
      }
    } else {
      this.propertiesForm.disable();
    }
  }

  /**
   * set form value
   */
  private setFormValue() {
    this.getControl('name').setValue(this.selectedProperty.name);
    this.getControl('dataTypeId').setValue(this.selectedProperty.dataTypeId);
    if (this.selectedProperty.propertyType === 'String' || this.selectedProperty.propertyType === 'Text' || this.selectedProperty.propertyType === 'Coordinates DD') {
      this.getControl('text').setValue(this.selectedProperty.value);
    } else if (this.selectedProperty.propertyType === 'Datetime') {
      const value = this.selectedProperty.value ? new Date(this.selectedProperty.value) : '';
      this.getControl('dateTime').setValue(value);
    }
  }

  /**
   * on reset property
   */
  onResetProperty() {
    this.propertyService.deleteProperty("assets", this.selectedProperty?.entityId, "properties", this.selectedProperty.id);
    this.dismiss();
  }

  /**
   * save/update property
   * @param createSingle create single
   */
  addProperty(createSingle = false) {
    this.previousPropertyName = this.getControl('name').value;
    const name = this.getControl('name').value;
    const dataTypeId = this.getControl('dataTypeId').value;
    let value = '';
    if (this.selectedPropertyType.name === 'Datetime') {
      value = this.getControl('dateTime').value?.toString();
    } else {
      value = this.getControl('text') ? this.getControl('text').value : '';
    }
    const property: Property = {
      ...this.selectedProperty,
      name,
      value,
      dataTypeId,
      isOverride: this.isOverride,
      action: 'property',
    };

    this.dynamicDrawerService.passOuptut(property, createSingle);
    this.clearForm();
  }

  /**
   * on prop type change
   */
  propTypeChanged() {
    const typeId: string = this.getControl('dataTypeId').value;
    console.log('typeId: ', typeId);
    const propertyType = this.propertyTypes.find(p => p.id === typeId);
    // remove dynamic control
    this.removeDynamicControl();
    if (propertyType) {
      // set dynamic control
      this.setDynamicControl(propertyType?.name);
      this.selectedPropertyType = propertyType;
    }

    if (this.isModelPage && typeId === EPropertyTypes.CoordinatesDD) {
      // this.getControl('text').disable();
    }

    this.onNameInput(this.getControl('name').value ? this.getControl('name').value : '');
  }

  //#endregion

  //#region add, remove and set dynamic controls to form

  /**
   * set dynamic controls
   * @param type prop type
   */
  private setDynamicControl(type: string) {
    switch (type) {
      case 'Datetime':
        this.dynamicControls = {
          'dateTime': ['']
        };
        break;

      case 'String':
      case 'Text':
      case 'Coordinates DD':
        this.dynamicControls = {
          'text': ['']
        };
        break;

      default:
        break;
    }

    // add dynamic controls
    this.addDynamicControl();
  }

  /**
   * add dynamic control to the form
   */
  private addDynamicControl() {
    for (let [key, val] of Object.entries(this.dynamicControls)) {
      this.propertiesForm.addControl(key, this.formBuilder.control(val[0]));
    }
  }

  /**
 * remove dynamic control from the form
 */
  private removeDynamicControl() {
    for (let key of Object.keys(this.dynamicControls)) {
      this.propertiesForm.removeControl(key);
    }
  }

  /**
 * get form control
 * @param prop form attribute name
 */
  getControl(attribute: string) {
    return this.propertiesForm.get(attribute) as FormControl;
  }

  //#endregion

  //#region Property autocomplete functionality and its validation

  /**
* get tags names
*/
  getPropertiesNames() {
    this.propertyService.getPropertyNames().pipe(take(1)).subscribe((PropertyNames: ITagsPropsName[]) => {
      if (PropertyNames.length) {
        console.log('PropertyNames: ', PropertyNames);
        if (this.selectedProperty) {
          PropertyNames = PropertyNames.filter((tagName: ITagsPropsName) => tagName.name !== this.selectedProperty.name);
        }

        this.propertyNames = [...PropertyNames];
        this.autoCompletePropName();
      }
    });
  }

  /**
   * auto complete on Property name
   */
  autoCompletePropName() {
    const properties$ = of(this.propertyNames);
    this.filteredPropertyNames = combineLatest([properties$, this.propertiesForm.controls['name']?.valueChanges.pipe(startWith(this.selectedProperty ? this.selectedProperty.name : ''))
    ]).pipe(map(([properties, value]) => {
      const lowercaseValue: string = value?.toLowerCase() || '';
      if (lowercaseValue) {
        return lowercaseValue ? properties?.filter((property: ITagsPropsName) => property.name.toLowerCase().includes(lowercaseValue)) : [...properties];
      }
    }));
  }

  /**
   * input event to check if name already exists
   * @param text typed text
   */
  onNameInput(text: string) {
    const selectedEntityProperties: string[] = this.dataService._properties.getValue().map((property: Property) => property.name.toLowerCase());
    if (selectedEntityProperties.includes(text.toLowerCase())) {
      this.propertiesForm.controls['name'].setErrors({ 'duplicate': true });
      this.propertiesForm.controls['name'].markAsTouched();
    } else {
      this.propertiesForm.controls['name'].setErrors(null);
      if (!text) {
        this.propertiesForm.controls['name'].setErrors(Validators.required);
        this.propertiesForm.controls['name'].markAsTouched();
      } else {
        const regexExpression: RegExp = /^[a-zA-Z]([a-zA-Z0-9_/-\s_.])+$/; // enter more than 1 letters where first can't be a number
        if (regexExpression.test(text)) {
          return true;
        } else {
          this.propertiesForm.controls['name'].setErrors({ 'badExpression': true });
          this.propertiesForm.controls['name'].markAsTouched();
        }
      }
    }
  }

  //#endregion

  //#region clear form and dismiss component

  /**
   * dismiss component
   */
  dismiss() {
    this.dynamicDrawerService.close(true);
    this.clearForm();
  }

  /**
   * clear form
   */
  private clearForm() {
    this.dataService.setEditMode(false);
    this.dataService.setProperty({} as Property);
    this.propertiesForm.reset();
    this.removeDynamicControl();
  }

  //#endregion

  ngOnDestroy() {
    this.destroyed.next();
    this.destroyed.complete();
  }
}
